import { inject, Injectable, Injector } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { filter, take } from "rxjs/operators";
import { EfactureAuthService } from "@jack-sparrow-libraries/facture-electronique";
import { LayoutService } from "@core/layout/services/layout.service";

const CLE_ROUTE_RETOUR = "efacture-route-retour";
const CLE_TENTATIVES = "efacture-tentatives-login";

type RouteRetour = { readonly url: string; readonly ts: number };

/**
 * Le login efacture est une navigation top-level (`window.location` vers
 * `{apiEfacture}/auth/login`) et le back revient sur le `redirect-page` fourni.
 * Or l'app AY ne survit pas a un chargement direct sur une route V2 : les URLs
 * `/app/v2/**` n'existent que via pushState, Struts sert son action par defaut et
 * impose sa propre navigation vers le dashboard.
 *
 * On fait donc revenir le back sur `/app` (URL reellement servie par le back
 * Java), on laisse Struts demarrer normalement, puis on rejoue la route Angular
 * memorisee.
 *
 * Il faut bien laisser Struts aller au bout de son dashboard : son initialisation
 * finit par `eMenuLink("dashboard")` -> `routingV1()`, qui sur un hote AgatheYou
 * (`location.host.includes("agatheyou")`) emet un `angular-navigate` vers
 * `/app/v1` (et non un `hashchange`, reserve a l'hote emotion). C'est cette
 * navigation qui declenche `initDashboard()` cote Struts, donc `loadMenu()` +
 * `loadFullDashboard()`, donc l'evenement `emotion-inited` — et c'est lui qui
 * conditionne l'affichage du layout AY (`AppLayoutComponent.$showLayout`).
 * Court-circuiter cette navigation fait perdre le menu. On attend donc
 * `emotion-inited` avant de naviguer : l'utilisateur voit brievement le dashboard.
 *
 * Rustine assumee : elle ne couvre pas le F5 sur une route V2. Le correctif de
 * fond est de supprimer le rechargement (login en popup + `onUnauthorized`
 * asynchrone avec rejeu de la requete, cote lib).
 */
@Injectable({ providedIn: "root" })
export class EfactureRetourService {
	private readonly router = inject(Router);
	private readonly authEfacture = inject(EfactureAuthService);
	private readonly layoutService = inject(LayoutService);
	private readonly injector = inject(Injector);

	// Garde-fou : si le cookie de session efacture n'est pas transmis (SameSite /
	// cookie tiers bloque), le retour re-declenche un 401 donc un nouveau login. On
	// borne les tentatives par onglet pour ne jamais boucler entre accueil et SSO.
	private static readonly MAX_TENTATIVES = 2;

	// Au-dela, la route memorisee est consideree perimee : on ne veut pas teleporter
	// l'utilisateur vers les e-factures sur un F5 sans rapport, plus tard. Large,
	// parce qu'il faut y inclure le temps passe sur la mire SSO.
	private static readonly VALIDITE_MS = 60_000;

	/** Branche sur `onUnauthorized` de la lib : memorise la route puis part au login. */
	demarrerLogin(): void {
		const tentatives = Number(sessionStorage.getItem(CLE_TENTATIVES) ?? "0");
		if (tentatives >= EfactureRetourService.MAX_TENTATIVES) {
			console.error(
				`[efacture] Authentification abandonnee apres ${tentatives} tentatives : le cookie de session efacture n'est pas transmis.`,
			);
			return;
		}

		sessionStorage.setItem(CLE_TENTATIVES, String(tentatives + 1));
		const retour: RouteRetour = { url: this.router.url, ts: Date.now() };
		sessionStorage.setItem(CLE_ROUTE_RETOUR, JSON.stringify(retour));
		this.authEfacture.triggerLogin(`${window.location.origin}/app`);
	}

	/** A appeler au demarrage de l'app : rejoue la route si on revient du login efacture. */
	restaurerRouteApresLogin(): void {
		const brut = sessionStorage.getItem(CLE_ROUTE_RETOUR);
		if (!brut) {
			return;
		}
		sessionStorage.removeItem(CLE_ROUTE_RETOUR);

		const retour = this.parse(brut);
		if (!retour || Date.now() - retour.ts > EfactureRetourService.VALIDITE_MS) {
			return;
		}

		this.quandLayoutPret(() => void this.router.navigateByUrl(retour.url));
	}

	/**
	 * Naviguer avant que Struts n'ait fini son dashboard nous ferait perdre le menu
	 * (cf. l'entete de cette classe) : on attend le signal alimente par
	 * `emotion-inited`.
	 */
	private quandLayoutPret(action: () => void): void {
		if (this.layoutService.$emotionInited()) {
			action();
			return;
		}
		toObservable(this.layoutService.$emotionInited, { injector: this.injector })
			.pipe(filter(Boolean), take(1))
			.subscribe(() => action());
	}

	private parse(brut: string): RouteRetour | null {
		try {
			return JSON.parse(brut) as RouteRetour;
		} catch {
			return null;
		}
	}
}
